// 工具函数
import { getApiBaseUrl, resolveApiUrl } from './apiBaseUrl'

// 修复图片 URL
export function fixImageUrl(url) {
  if (!url) return ''
  let path = url.replace(/\\/g, '/')
  if (path.startsWith('http')) return path
  if (path.includes('uploads')) {
    path = '/' + path.substring(path.indexOf('uploads'))
    return resolveApiUrl(path)
  }
  return resolveApiUrl(path.startsWith('/') ? path : '/' + path)
}

// 格式化数字
export function formatNumber(num) {
  return num >= 1000 ? (num / 1000).toFixed(1) + 'k' : num
}

// 防抖函数
export function debounce(func, delay) {
  let timeout
  return function(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), delay)
  }
}

// 下载图片
export function downloadImage(url, name) {
  const a = document.createElement('a')
  a.href = url
  a.download = (name ? name.substring(0, 20) : 'ai_image') + '.png'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** 从任意图片 URL 中提取 /uploads/... 相对路径（便于同源 fetch） */
export function extractUploadsPath(url) {
  const s = String(url || '').replace(/\\/g, '/')
  const idx = s.indexOf('/uploads/')
  if (idx >= 0) return s.slice(idx).split(/[?#]/)[0]
  const loose = s.indexOf('uploads/')
  if (loose >= 0) return `/${s.slice(loose).split(/[?#]/)[0]}`
  return ''
}

function getAuthToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
}

function isLikelyImageBlob(blob) {
  if (!blob || !blob.size || blob.size < 16) return false
  const mime = String(blob.type || '').toLowerCase()
  if (mime.startsWith('image/')) return true
  if (/json|html|text\/plain|javascript/.test(mime)) return false
  return mime === '' || mime === 'application/octet-stream'
}

function loadImageUrlAsBlobViaCanvas(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('canvas unsupported'))
          return
        }
        ctx.drawImage(img, 0, 0)
        canvas.toBlob(blob => {
          if (blob && blob.size > 0) resolve(blob)
          else reject(new Error('toBlob failed'))
        }, 'image/png')
      } catch (err) {
        reject(err)
      }
    }
    img.onerror = () => reject(new Error('image load failed'))
    img.src = imageUrl
  })
}

async function fetchUrlAsImageBlob(u, token) {
  const headers = token ? { Authorization: token } : {}
  const res = await fetch(u, { credentials: 'include', headers })
  if (!res.ok) return null
  const blob = await res.blob()
  return isLikelyImageBlob(blob) ? blob : null
}

async function readProxyErrorMessage(res) {
  try {
    const text = await res.text()
    if (!text) return `HTTP ${res.status}`
    try {
      const json = JSON.parse(text)
      const msg = json && (json.message || json.msg || json.error)
      if (msg) return String(msg).slice(0, 160)
    } catch {
      /* plain text */
    }
    return text.slice(0, 160)
  } catch {
    return `HTTP ${res.status}`
  }
}

/** 经后端 /proxy_media_download 拉取图片（绕过 CORS；POST 避免超长 query） */
export async function fetchImageBlobViaProxy(url) {
  const raw = String(url || '').trim()
  if (!raw) throw new Error('empty url')
  const token = getAuthToken()
  const authHeaders = token ? { Authorization: token } : {}

  const parseBlob = async res => {
    if (!res.ok) {
      throw new Error(await readProxyErrorMessage(res))
    }
    const blob = await res.blob()
    if (!isLikelyImageBlob(blob)) {
      const text = await blob.text().catch(() => '')
      throw new Error(text.slice(0, 120) || 'proxy empty blob')
    }
    return blob
  }

  let res = await fetch(resolveApiUrl('/proxy_media_download'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders
    },
    body: JSON.stringify({ url: raw }),
    credentials: 'include'
  })

  if (res.status === 404 || res.status === 405) {
    const qs = new URLSearchParams({ url: raw })
    res = await fetch(`${resolveApiUrl('/proxy_media_download')}?${qs}`, {
      method: 'GET',
      headers: authHeaders,
      credentials: 'include'
    })
  }

  return parseBlob(res)
}

/** 拉取图片 Blob：后端代理优先（跨域 CDN），再尝试同源 fetch / Canvas */
export async function fetchImageBlobForDownload(url) {
  const raw = String(url || '').trim()
  if (!raw) throw new Error('empty url')

  if (/^(data:|blob:)/i.test(raw)) {
    const res = await fetch(raw)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    if (isLikelyImageBlob(blob)) return blob
    throw new Error('invalid image data')
  }

  const candidates = []
  const uploadsPath = extractUploadsPath(raw)
  if (uploadsPath) {
    candidates.push(uploadsPath)
  }
  candidates.push(raw)
  if (uploadsPath) {
    candidates.push(fixImageUrl(uploadsPath))
  }
  candidates.push(fixImageUrl(raw))
  if (raw.startsWith('/')) candidates.push(raw)

  const urls = [...new Set(candidates.filter(Boolean))]
  const token = getAuthToken()
  let lastError = null

  if (/^https?:\/\//i.test(raw) || uploadsPath) {
    try {
      return await fetchImageBlobViaProxy(raw)
    } catch (err) {
      lastError = err
    }
  }

  for (const u of urls) {
    try {
      const blob = await fetchUrlAsImageBlob(u, token)
      if (blob) return blob
    } catch (err) {
      lastError = err
    }
  }

  for (const u of urls) {
    try {
      return await loadImageUrlAsBlobViaCanvas(u)
    } catch (err) {
      lastError = err
    }
  }

  throw lastError || new Error('download failed')
}

/** 将图片 URL 下载到本地文件 */
export async function downloadImageFile(url, filename) {
  const blob = await fetchImageBlobForDownload(url)
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename || 'image.png'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objUrl)
}

/** 从已渲染的 img 元素导出 Blob（同源或未污染时可用） */
export async function blobFromLoadedImageElement(img) {
  if (!img || !img.naturalWidth || !img.naturalHeight) return null
  try {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(img, 0, 0)
    return await new Promise(resolve => {
      canvas.toBlob(blob => resolve(blob && blob.size > 0 ? blob : null), 'image/png')
    })
  } catch {
    return null
  }
}

